import { useEffect, useMemo } from "react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";

import SharedEndDatePicker from "./SharedEndDatePicker";
import {
  REASON_TARGET_GROUP_NONE_SELECT_VALUE,
  type ReasonPanelProps,
} from "./reasonPanel.types";
import { useGroupsByOfficeQuery } from "@/hooks/useGroupsByOfficeQuery";

export default function TransferReasonPanel({
  joiningDate,
  startDate,
  onStartDateChange,
  isStartDateError,
  targetGroupId,
  onTargetGroupIdChange,
  isTargetGroupError,
  memo,
  onMemoChange,
  excludeTargetGroupId,
  disabled,
  useOfficeGroupsForTarget,
  officeIdForTargetGroups,
  occupiedRanges,
}: ReasonPanelProps) {
  const oid = (officeIdForTargetGroups ?? "").trim();
  const useOffice = useOfficeGroupsForTarget === true && oid.length > 0;

  const { data: officeGroups } = useGroupsByOfficeQuery({
    enabled: useOffice,
    officeId: oid || undefined,
  });

  const excludeId = excludeTargetGroupId?.trim() ?? "";
  const groups = useMemo(() => {
    const raw = useOffice ? (officeGroups ?? []) : [];
    return excludeId === "" ? raw : raw.filter((g) => g.group_id !== excludeId);
  }, [useOffice, officeGroups, excludeId]);

  useEffect(() => {
    if (!excludeId || !targetGroupId) return;
    if (targetGroupId === excludeId) {
      onTargetGroupIdChange?.("");
    }
  }, [excludeId, targetGroupId, onTargetGroupIdChange]);

  const targetGroupSelectValue = useMemo(() => {
    const raw = (targetGroupId ?? "").trim();
    return raw === "" ? REASON_TARGET_GROUP_NONE_SELECT_VALUE : raw;
  }, [targetGroupId]);

  return (
    <div className="flex flex-col gap-3 pt-2">
      <SharedEndDatePicker
        label="병동이동 시작일"
        joiningDate={joiningDate}
        value={startDate}
        onChange={onStartDateChange}
        isError={isStartDateError}
        required
        disabled={disabled}
        occupiedRanges={occupiedRanges}
      />

      <div className="flex flex-col gap-2">
        <label className="text-[14px] font-semibold text-[#939393]">
          이동 병동 <span className="text-[#C0392B]">*</span>
        </label>
        <Select
          value={targetGroupSelectValue}
          onValueChange={(value) =>
            onTargetGroupIdChange?.(
              value === REASON_TARGET_GROUP_NONE_SELECT_VALUE ? "" : value
            )
          }
          disabled={disabled ?? false}
        >
          <SelectTrigger
            className={cn(
              "w-full text-[14px]",
              isTargetGroupError ? "border-destructive" : "border-[#D6D6DE]"
            )}
          >
            <SelectValue placeholder="선택 안 함" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={REASON_TARGET_GROUP_NONE_SELECT_VALUE}>
              선택 안 함
            </SelectItem>
            {groups.map((g) => (
              <SelectItem key={g.group_id} value={g.group_id}>
                {g.group_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isTargetGroupError && (
          <p className="text-destructive text-xs">* 필수 값입니다</p>
        )}
      </div>

      <Textarea
        className="resize-none min-h-[64px] text-sm"
        placeholder="병동이동 관련 메모"
        value={memo ?? ""}
        onChange={(e) => onMemoChange(e.target.value)}
        disabled={(disabled ?? false) || !startDate}
      />
    </div>
  );
}
